// Benchmarking chart — Strategy vs Buy & hold percent-return curves on the same time
// axis. Mirrors TradingView's "Benchmarking" panel with floating Max/Current/Min badges.
//
// Both curves are normalised to percent gain from base 100 (i.e. value - 100) so the
// comparison is apples-to-apples regardless of starting equity.

import { useEffect, useMemo, useRef } from "react";
import {
  createChart,
  type IChartApi,
  type ISeriesApi,
  type Time,
  LineSeries,
} from "lightweight-charts";
import type { BacktestResponseDto } from "../../api/types";

interface Props {
  result: BacktestResponseDto;
  height?: number;
}

interface Curve {
  label: string;
  equityByBar: number[];
  buyHoldByBar: number[];
  barTimes: string[];
}

function pickCurve(result: BacktestResponseDto): Curve | null {
  if (result.strategy && result.strategy.equityByBar.length > 0) {
    return {
      label: "Netting",
      equityByBar: result.strategy.equityByBar,
      buyHoldByBar: result.strategy.buyHoldByBar,
      barTimes: result.strategy.barTimes,
    };
  }
  if (result.merged && result.merged.equityByBar.length > 0) {
    return { label: "Merged", ...result.merged };
  }
  if (result.long && result.long.equityByBar.length > 0) return { label: "Long", ...result.long };
  if (result.short && result.short.equityByBar.length > 0) return { label: "Short", ...result.short };
  return null;
}

// Shared UTC-defensive parser (see hooks/time) so equity points aren't TZ-shifted on a zone-less ts.
import { toChartTime as toUtcChartSeconds } from "../../hooks/time";
const toChartTime = (iso: string): Time => (Math.floor(toUtcChartSeconds(iso)) as unknown as Time);

export function BenchmarkingChart({ result, height = 220 }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const stratSeries = useRef<ISeriesApi<"Line"> | null>(null);
  const bhSeries = useRef<ISeriesApi<"Line"> | null>(null);

  const curve = useMemo(() => pickCurve(result), [result]);

  const summary = useMemo(() => {
    if (!curve) return null;
    const stratPct = curve.equityByBar.map((v) => v - 100);
    const bhPct = curve.buyHoldByBar.map((v) => v - 100);
    return {
      strat: {
        max: Math.max(...stratPct),
        current: stratPct[stratPct.length - 1] ?? 0,
        min: Math.min(...stratPct),
      },
      bh: {
        max: Math.max(...bhPct),
        current: bhPct[bhPct.length - 1] ?? 0,
        min: Math.min(...bhPct),
      },
    };
  }, [curve]);

  useEffect(() => {
    if (!containerRef.current) return;
    chartRef.current = createChart(containerRef.current, {
      autoSize: true,
      layout: { background: { color: "#0d1117" }, textColor: "#c9d1d9" },
      grid: { vertLines: { color: "#161b22" }, horzLines: { color: "#161b22" } },
      timeScale: { borderColor: "#30363d", timeVisible: true, secondsVisible: false },
      rightPriceScale: { borderColor: "#30363d" },
      crosshair: { mode: 0 },
    });
    bhSeries.current = chartRef.current.addSeries(LineSeries, {
      color: "#d29922",
      lineWidth: 1,
      title: "P&L for buy & hold",
    });
    stratSeries.current = chartRef.current.addSeries(LineSeries, {
      color: "#58a6ff",
      lineWidth: 2,
      title: "P&L for strategy",
    });
    return () => {
      chartRef.current?.remove();
      chartRef.current = null;
      stratSeries.current = null;
      bhSeries.current = null;
    };
  }, []);

  useEffect(() => {
    if (!stratSeries.current || !bhSeries.current || !curve) return;
    const points = curve.equityByBar.map((v, i) => ({
      t: toChartTime(curve.barTimes[i]),
      strat: v - 100,
      bh: curve.buyHoldByBar[i] - 100,
    }));
    points.sort((a, b) => (a.t as number) - (b.t as number));
    const dedup: typeof points = [];
    let lastT = -1;
    for (const p of points) {
      if ((p.t as number) === lastT) continue;
      dedup.push(p);
      lastT = p.t as number;
    }
    stratSeries.current.setData(dedup.map((p) => ({ time: p.t, value: p.strat })));
    bhSeries.current.setData(dedup.map((p) => ({ time: p.t, value: p.bh })));
    chartRef.current?.timeScale().fitContent();
  }, [curve]);

  if (!curve || !summary) {
    return (
      <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
        No benchmarking data.
      </div>
    );
  }

  return (
    <div style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Benchmarking</div>
      <div style={{ position: "relative" }}>
        <div ref={containerRef} style={{ height, border: "1px solid #30363d", borderRadius: 4 }} />
        {/* Floating Max / Current / Min badges, top-right */}
        <div style={{ position: "absolute", top: 8, right: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          <SummaryBadge color="#d29922" label="Buy & hold" data={summary.bh} />
          <SummaryBadge color="#58a6ff" label="Strategy" data={summary.strat} />
        </div>
      </div>
    </div>
  );
}

function SummaryBadge({ color, label, data }: { color: string; label: string; data: { max: number; current: number; min: number } }) {
  return (
    <div style={{
      background: "rgba(13,17,23,0.85)",
      border: `1px solid ${color}`,
      borderRadius: 4,
      padding: "4px 8px",
      fontSize: 10,
      fontFamily: "monospace",
      color: "#c9d1d9",
      minWidth: 140,
    }}>
      <div style={{ color, fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: "#8b949e" }}>Max</span><span>{fmtPct(data.max)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: "#8b949e" }}>Current</span><span>{fmtPct(data.current)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: "#8b949e" }}>Min</span><span>{fmtPct(data.min)}</span>
      </div>
    </div>
  );
}

function fmtPct(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}
